import { Alert, AlertTitle, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { NEVER_GRANTABLE_REASON } from "library/grants/never-grantable.mjs"
import { BundleDemandChip } from "./bundle-demand-chip"

/**
 * What the chosen auth bundles will actually confer, between them (FIQ-1926).
 *
 * The union is shown rather than a per-bundle breakdown because the union is what the override
 * confers; which of two overlapping bundles supplied a demand changes nothing about the result.
 *
 * A bundle is trusted as a whole with one exception — `admin` — and the exception is explained
 * here rather than merely applied. Someone selecting a bundle is trying to sell a customer a
 * capability, not escalate anything; a demand that silently vanishes between the bundle and the
 * override reads as a broken tool, and they would have no way to find out otherwise until the
 * customer told them the capability never arrived.
 */
export function BundleDemandPreview({ bundles }) {
    const chosen = bundles ?? []
    const demands = [...new Set(chosen.flatMap(demandsOf))]
    const refused = [...new Set(chosen.flatMap(droppedDemandsOf))]

    return (
        <Stack spacing={1} data-cy="grant-bundle-preview">
            <Typography variant="caption" color="text.secondary">
                These bundles confer:
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                <Repeat list={demands} item={<BundleDemandChip />} />
            </Stack>
            <If value={demands.length === 0}>
                <Typography variant="caption" color="text.secondary">
                    These bundles contain no demands that can be conferred, so they would add nothing to the override.
                </Typography>
            </If>
            <If value={refused.length > 0}>
                <Alert severity="info" data-cy="grant-bundle-dropped">
                    <AlertTitle sx={{ mb: 0.5 }}>Not conferred: {refused.join(", ")}</AlertTitle>
                    <Typography variant="body2">{NEVER_GRANTABLE_REASON}</Typography>
                </Alert>
            </If>
        </Stack>
    )
}

function demandsOf(bundle) {
    return bundle?.demands ?? []
}

function droppedDemandsOf(bundle) {
    return bundle?.droppedDemands ?? []
}
